/**
 * Header component
 *
 * App header with logo, navigation links, theme toggle, and user menu.
 * Navigation links are shown on desktop (lg+), hidden on mobile.
 * Uses top navigation bar pattern for cleaner desktop layout.
 *
 * V1-inspired: glass morphism backdrop, refined typography,
 * teal accent on brand mark.
 */
import { Link } from "@tanstack/react-router";
import { useAuth } from "@/providers/auth-provider";
import { UserMenu } from "@/components/auth/user-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { TopNavBar } from "./top-nav-bar";
import {
  getHeaderWithNavClasses,
  getHeaderInnerClasses,
  getHeaderLeftSectionClasses,
  getHeaderRightSectionClasses,
} from "./navigation-utils";

/**
 * App header with branding, navigation, and auth controls
 *
 * Layout:
 * - Left: Logo + Navigation links (desktop only)
 * - Right: Theme toggle + User menu (authenticated)
 */
export function Header() {
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <header className={getHeaderWithNavClasses()}>
      <div className={getHeaderInnerClasses()}>
        {/* Left side: Logo + Navigation */}
        <div className={getHeaderLeftSectionClasses()}>
          {/* Logo / App name -- teal accent on "line" matching V1 brand */}
          <Link
            to="/"
            hash=""
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <span className="text-xl font-bold tracking-tight text-foreground">
              Flo
              <span className="text-primary">line</span>
            </span>
          </Link>

          {/* Desktop navigation - only shown when authenticated */}
          {isAuthenticated && <TopNavBar />}
        </div>

        {/* Right side controls */}
        <div className={getHeaderRightSectionClasses()}>
          {/* Theme toggle */}
          <ThemeToggle />

          {/* Auth section */}
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
          ) : isAuthenticated ? (
            <UserMenu />
          ) : null}
        </div>
      </div>
    </header>
  );
}
